/**
 * Webflow OAuth authentication helper
 */

export interface WebflowOAuthConfig {
  siteId?: string
}

export interface WebflowOAuthResult {
  success: boolean
  error?: string
}

/**
 * Initiates Webflow OAuth flow in a popup window
 */
export async function initiateWebflowOAuth(
  config?: WebflowOAuthConfig
): Promise<WebflowOAuthResult> {
  return new Promise((resolve) => {
    const siteId = config?.siteId

    // Build authorization URL - use backend API endpoint
    const authUrl = siteId
      ? `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/oauth/webflow/authorize?siteId=${encodeURIComponent(siteId)}`
      : `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/oauth/webflow/authorize`

    // Open popup window
    const width = 600
    const height = 700
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2

    const popup = window.open(
      authUrl,
      'Webflow OAuth',
      `width=${width},height=${height},left=${left},top=${top}`
    )

    if (!popup) {
      resolve({
        success: false,
        error: 'Failed to open popup window. Please allow popups for this site.',
      })
      return
    }

    // Listen for messages via BroadcastChannel
    const channel = new BroadcastChannel('oauth_channel')
    channel.onmessage = (event) => {
      // Only handle Webflow OAuth messages
      if (!event.data || !event.data.type || !event.data.type.startsWith('WEBFLOW_OAUTH_')) {
        return
      }

      if (event.data.type === 'WEBFLOW_OAUTH_SUCCESS') {
        clearInterval(checkPopupClosed)
        channel.close()
        resolve({ success: true })
      } else if (event.data.type === 'WEBFLOW_OAUTH_ERROR') {
        clearInterval(checkPopupClosed)
        channel.close()
        resolve({
          success: false,
          error: event.data.error || 'OAuth authentication failed',
        })
      }
    }

    // Handle popup close without completion
    const checkPopupClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopupClosed)
        channel.close()
        resolve({
          success: false,
          error: 'OAuth window was closed before completing authentication',
        })
      }
    }, 1000)
  })
}
